import React from 'react';

const IntegrationsSection = ({ 
  artist, 
  telegramCode, 
  telegramCodeExpiresAt, 
  telegramCodeLoading,
  telegramCodeError,
  telegramCodeCopied,
  showTelegramModal,
  onShowTelegramModal,
  onGenerateTelegramCode,
  onCopyTelegramCode,
  onTestTelegram,
  formatDateTime 
}) => {
  const isTelegramConnected = !!artist?.telegram_id;

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2 className="settings-section-title">Intégrations</h2>
        <p className="settings-section-description">
          Connectez vos outils externes pour automatiser votre workflow
        </p>
      </div>

      {/* Telegram Integration — masqué pour les Free sans config */}
      {(artist?.subscription_tier === 'premium' || artist?.telegram_id) && (
        <div className="integration-card">
          <div className="integration-icon">
            <i className="fa-brands fa-telegram"></i>
          </div>
          <div className="integration-content">
            <h3 className="integration-name">Telegram Bot</h3>
            <p className="integration-description">
              Recevez des notifications et gérez vos releases directement depuis Telegram
            </p>
            <div className={`integration-status ${isTelegramConnected ? 'connected' : 'disconnected'}`}>
              <i className={`fa-solid fa-${isTelegramConnected ? 'circle-check' : 'circle-xmark'}`}></i>
              {isTelegramConnected ? (
                <span>Connecté (ID: <code>{artist.telegram_id}</code>)</span>
              ) : (
                'Non connecté'
              )}
            </div>
          </div>
          <div className="integration-actions">
            {isTelegramConnected && (
              <button 
                className="btn-ghost"
                onClick={onTestTelegram}
                title="Envoyer une notification de test"
                style={{ marginRight: '10px' }}
              >
                <i className="fa-solid fa-paper-plane" style={{ marginRight: '5px' }}></i>
                Tester
              </button>
            )}
            <button 
              className={`btn-${isTelegramConnected ? 'secondary' : 'primary'}`}
              onClick={async () => {
                onShowTelegramModal(true);
                if (!telegramCode) {
                  await onGenerateTelegramCode();
                }
              }}
            >
              {isTelegramConnected ? 'Reconfigurer' : 'Connecter'}
            </button>
          </div>
        </div>
      )}

      {/* More integrations placeholder */}
      <div className="integration-card" style={{ opacity: 0.6 }}>
        <div className="integration-icon" style={{ background: 'rgba(0,0,0,0.05)', color: 'var(--text-muted)' }}>
          <i className="fa-brands fa-spotify"></i>
        </div>
        <div className="integration-content">
          <h3 className="integration-name">Spotify for Artists</h3>
          <p className="integration-description">
            Synchronisez vos statistiques Spotify (bientôt disponible)
          </p>
        </div>
        <button className="btn-ghost" disabled>
          Bientôt
        </button>
      </div>

      <div className="integration-card" style={{ opacity: 0.6 }}>
        <div className="integration-icon" style={{ background: 'rgba(0,0,0,0.05)', color: 'var(--text-muted)' }}>
          <i className="fa-brands fa-youtube"></i>
        </div>
        <div className="integration-content">
          <h3 className="integration-name">YouTube Music</h3>
          <p className="integration-description">
            Connectez votre chaîne YouTube (bientôt disponible)
          </p>
        </div>
        <button className="btn-ghost" disabled>
          Bientôt
        </button>
      </div>

      {/* Telegram Modal - Using existing styles from dashboard.css */}
      {showTelegramModal && (
        <div 
          className="telegram-modal-backdrop"
          role="presentation"
          onClick={() => onShowTelegramModal(false)}
          style={{ zIndex: 3000 }}
        >
          <div
            className="telegram-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="telegram-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="telegram-modal-header">
              <div>
                <p className="telegram-modal-kicker">Connexion Telegram</p>
                <h3 id="telegram-modal-title" className="telegram-modal-title">
                  Configurer l&apos;appairage du bot
                </h3>
              </div>
              <button 
                className="btn-close-popup" 
                onClick={() => onShowTelegramModal(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="telegram-modal-body">
              <div className="telegram-modal-steps">
                <div className="telegram-modal-step">
                  <span className="step-num">1</span>
                  <p>Ouvre <strong>@PassioArtistBot</strong> dans Telegram.</p>
                </div>
                <div className="telegram-modal-step">
                  <span className="step-num">2</span>
                  <p>Envoie la commande <code>/start</code> suivie du code affiché ci-dessous.</p>
                </div>
                <div className="telegram-modal-step">
                  <span className="step-num">3</span>
                  <p>Une fois le code validé, le bot te saluera par ton nom.</p>
                </div>
              </div>

              <div className="telegram-code-panel">
                <p className="telegram-code-label">Code temporaire</p>
                {telegramCode ? (
                  <>
                    <div className="telegram-code-box">{telegramCode}</div>
                    <p className="telegram-code-expiry">
                      Valable jusqu&apos;au {formatDateTime(telegramCodeExpiresAt)}
                    </p>
                    <div className="telegram-code-actions">
                      <button className="btn-copy-code" onClick={onCopyTelegramCode}>
                        {telegramCodeCopied ? 'Code copié' : 'Copier le code'}
                      </button>
                      <button
                        className="btn-telegram-generate btn-telegram-generate-secondary"
                        onClick={onGenerateTelegramCode}
                        disabled={telegramCodeLoading}
                      >
                        {telegramCodeLoading ? 'Génération...' : 'Régénérer'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="telegram-code-empty">
                      {telegramCodeLoading ? 'Génération du code en cours...' : 'Le code apparaîtra ici après génération.'}
                    </div>
                    <p className="telegram-code-expiry">Valable 10 minutes, usage unique.</p>
                  </>
                )}
              </div>

              {telegramCodeError && <p className="telegram-link-error">{telegramCodeError}</p>}

              <div className="telegram-modal-footer">
                <p className="telegram-link-note">
                  Le nouveau code remplace automatiquement l&apos;ancien tant qu&apos;il n&apos;a pas été consommé.
                </p>
                <a
                  href="https://t.me/PassioArtistBot"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-telegram-open"
                >
                  Ouvrir Telegram
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsSection;
